import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { SanityYoutubeVideo } from "../../model/common";
import AspectRatio from "../AspectRatio/AspectRatio";
import Media from "../MediaBlock/MediaBlock";

const enum PlayerState {
  "Unstarted" = -1,
  "Ended" = 0,
  "Playing" = 1,
  "Paused" = 2,
  "Buffering" = 3,
  "VideoCued" = 5
}

interface YoutubeInfoMessage {
  event: "infoDelivery";
  info: {
    progressState?: {
      current?: number;
      duration?: number;
    };
    playerState?: PlayerState;
  };
}

interface YoutubeDeliveryMessage {
  event: "initialDelivery";
  info: {
    videoUrl: string;
    duration: number;
    videoData: {
      title: string;
    };
  };
}

interface YoutubeAnalytics {
  video_current_time: number;
  video_duration: number;
  video_percent: number;
  video_provider: string;
  video_title: string;
  video_url: string;
  visible: boolean;
}

type YoutubeMessage = YoutubeInfoMessage | YoutubeDeliveryMessage;

interface VideoState {
  started: boolean;
  percentageViewed: Record<number, boolean>;
}

export interface YoutubeVideoProps {
  className?: string;
  node: SanityYoutubeVideo;
}

const YoutubeVideo = ({ className, node }: YoutubeVideoProps) => {
  const { alt, attribution, caption, youtubeId } = node;
  const ref = useRef<HTMLIFrameElement>(null);
  const [prevPlayerState, setPrevPlayerState] = useState(5);
  const defaultVideoState = useMemo(
    () => ({
      started: false,
      percentageViewed: {
        10: false,
        25: false,
        50: false,
        75: false
      }
    }),
    []
  );
  const [commonVideoInfo, setCommonVideoInfo] = useState<
    Partial<YoutubeAnalytics>
  >({});
  const [videoState, setVideoState] = useState<VideoState>(defaultVideoState);

  const sendAnalytics = useCallback(
    (eventName: string, data: Partial<YoutubeAnalytics>) => {
      const gtag = (window as any).gtag;
      const boundingRect = ref.current!.getBoundingClientRect();
      const midline = boundingRect.top + boundingRect.height / 2;
      const youtubeAnalytics = {
        ...commonVideoInfo,
        ...data,
        // If half the video is showing vertically in the viewport, count it as visible.
        visible:
          (midline < window.innerHeight && midline > 0) ||
          (midline > 0 && midline < window.innerHeight)
      };
      if (gtag) {
        gtag("event", eventName, youtubeAnalytics);
      }
    },
    [commonVideoInfo]
  );

  useEffect(() => {
    const listener = (e: MessageEvent) => {
      if (
        e.origin === "https://www.youtube-nocookie.com" &&
        ref.current?.contentWindow === e.source
      ) {
        const data = JSON.parse(e.data) as YoutubeMessage;
        if (!data.info) {
          return;
        }

        if (data.event === "initialDelivery") {
          setCommonVideoInfo({
            video_current_time: 0,
            // Initial duration is different to the duration value when the video is played.
            // This is updated when the video is started.
            video_duration: data.info.duration,
            video_percent: 0,
            video_provider: "youtube",
            video_title: data.info.videoData.title,
            video_url: data.info.videoUrl
          });
          return;
        }

        if (
          data.info.progressState?.current &&
          data.info.progressState?.duration
        ) {
          const currentProgress =
            (data.info.progressState.current /
              (commonVideoInfo.video_duration ??
                data.info.progressState?.duration)) *
            100;

          const availableValues: number[] = Object.entries(
            videoState.percentageViewed
          )
            .filter(([, value]) => !value)
            .map(([key]) => parseInt(key));

          for (const value of availableValues) {
            if (currentProgress >= value) {
              if (
                prevPlayerState === PlayerState.Playing &&
                data.info.playerState !== PlayerState.Paused
              ) {
                sendAnalytics("video_progress", {
                  video_current_time: Math.round(
                    data.info.progressState.current
                  ),
                  video_percent: value
                });
              }
              setVideoState(prevState => ({
                ...prevState,
                percentageViewed: {
                  ...prevState.percentageViewed,
                  [value]: true
                }
              }));
            }
          }
        }

        if (
          data.info.playerState === PlayerState.Playing &&
          data.info.progressState?.duration
        ) {
          if (!videoState.started) {
            setVideoState(prevState => ({
              ...prevState,
              started: true
            }));
            const duration = Math.round(data.info.progressState.duration);
            setCommonVideoInfo(prevState => ({
              ...prevState,
              // Update video duration to correct value. This is the value used in percentage viewed calculations.
              video_duration: duration
            }));
            sendAnalytics("video_start", {
              video_duration: duration
            });
          }
        }

        if (
          data.info.playerState === PlayerState.Ended &&
          data.info.progressState?.current
        ) {
          setVideoState(defaultVideoState);
          sendAnalytics("video_end", {
            video_current_time: Math.round(data.info.progressState.current),
            video_percent: 100
          });
        }

        if (data.info.playerState) {
          setPrevPlayerState(data.info.playerState);
        }
      }
    };
    if (ref.current?.contentWindow) {
      window.addEventListener("message", listener);
    }
    return () => {
      window.removeEventListener("message", listener);
    };
  }, [
    commonVideoInfo.video_duration,
    defaultVideoState,
    prevPlayerState,
    sendAnalytics,
    videoState.percentageViewed,
    videoState.started
  ]);

  // Tells Youtube Iframe to send messages on state updates.
  const sendListenCommand = useCallback(() => {
    ref.current?.contentWindow?.postMessage(
      JSON.stringify({ event: "listening" }),
      "*"
    );
  }, []);
  return (
    <Media
      className={className}
      attribution={attribution}
      caption={caption}
      mediaItem={
        <AspectRatio className="video" ratio={16 / 9}>
          {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
          <iframe
            ref={ref}
            // Avoid youtube cookie. rel=0 should limit related videos to our channel.
            // Once we have translated videos we can try e.g. cc_lang_pref=fr
            // but we'll need to check our codes match theirs.
            src={`https://www.youtube-nocookie.com/embed/${
              youtubeId ? youtubeId.trim() : ""
            }?rel=0&cc_load_policy=1&enablejsapi=1`}
            allow="encrypted-media"
            frameBorder="0"
            allowFullScreen
            title={alt}
            onLoad={sendListenCommand}
          />
        </AspectRatio>
      }
    />
  );
};

export default YoutubeVideo;
